import React, { useState, useRef, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import {
  Button,
  Form,
  Row,
  Col,
  DatePicker,
  Select,
  Input,
  Modal,
  Divider,
} from "antd"
import { ArrowLeftOutlined } from "@ant-design/icons"
import TextArea from "antd/es/input/TextArea"
import { Container } from "react-bootstrap"
import { usePostRequest } from "../../hooks/usePostRequest"
import { UsePostRequest } from "../../utils/api/ApiRequests"
import { getUser, getToken, capitalizeEachWord, getIsEmployeeLogin, getEmployeeId } from "../../utils/Common"
import Navbar from "../../components/Navbar/Navbar"
import logo2 from "../../assets/images/logo2.png"
import dayjs from "dayjs"
import SignatureCanvas from "react-signature-canvas"

// ─── Section Header ───────────────────────────────────────────────────────────
const SectionHeader = ({ title }) => (
  <Row className="mt-3 mb-1">
    <Col xs={24}>
      <span className="primary-color bold">{title}</span>
      <Divider className="mt-1 mb-0" />
    </Col>
  </Row>
)

// ─── Signature Pad Modal ──────────────────────────────────────────────────────
const SignaturePadModal = ({ open, onCancel, onSubmit, title }) => {
  const sigRef = useRef(null)
  const handleClear = () => sigRef.current?.clear()
  const handleSubmit = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      onSubmit(sigRef.current.toDataURL("image/png"))
    }
  }

  return (
    <Modal
      open={open}
      title={<span className="primary-color">{title}</span>}
      onCancel={onCancel}
      footer={
        <Row className="d-flex justify-content-between">
          <Button onClick={handleClear}>Clear</Button>
          <Row className="gap-2">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}>
              Submit
            </Button>
          </Row>
        </Row>
      }
      centered
      width="90%"
      style={{ maxWidth: 480 }}
    >
      <div
        className="border rounded"
        style={{ background: "transparent", overflowX: "auto" }}
      >
        <SignatureCanvas
          ref={sigRef}
          penColor="black"
          canvasProps={{
            width: 430,
            height: 180,
            className: "sig-canvas",
            style: {
              background: "transparent",
              maxWidth: "100%",
              display: "block",
            },
          }}
        />
      </div>
      <p className="tiny-text mt-1">Draw your signature above</p>
    </Modal>
  )
}

// ─── Signature Box ────────────────────────────────────────────────────────────
const SignatureBox = ({ label, sig, name, onAdd }) => (
  <Col xs={24} sm={12}>
    <div className="p-3">
      <Row className="bold mb-2">{label}</Row>
      <div
        style={{
          height: 80,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          marginBottom: 4,
        }}
      >
        {sig ? (
          <img
            src={sig}
            alt="signature"
            style={{
              maxHeight: 80,
              maxWidth: "100%",
              background: "transparent",
            }}
          />
       ) : onAdd ? (
      <Button size="small" className="noPrint" onClick={onAdd}>
        Add Signature
      </Button>
      ) : null}
      </div>
      <div
        style={{ textAlign: "center", fontWeight: "bold", marginBottom: 2 }}
      >
        {name || "___________________________"}
      </div>
      <hr style={{ borderTop: "1px solid black", margin: "0 0 4px 0" }} />
      <div style={{ textAlign: "center", fontSize: "60%" }}>
        Signature over Printed Name
      </div>
    </div>
  </Col>
)

// ─── Main Component ───────────────────────────────────────────────────────────
export default function AddLoan() {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const componentRef = useRef()
  const dateFormat = "YYYY-MM-DD"

  const { callPost, isLoading } = usePostRequest(() => {
  navigate("/loans")
  })
  const isEmployeeLogin = getIsEmployeeLogin()
  const employeeId = getEmployeeId()

  const [allEmployees, setAllEmployees] = useState([])
  const [selectedEmployeeName, setSelectedEmployeeName] = useState("")
  const [selectedPosition, setSelectedPosition] = useState("")
  const [requesterSig, setRequesterSig] = useState(null)
  const [approverSig, setApproverSig] = useState(null)
  const [showRequesterSigModal, setShowRequesterSigModal] = useState(false)
  const [showApproverSigModal, setShowApproverSigModal] = useState(false)
  const [approverName, setApproverName] = useState("")
  const [selectedTerm, setSelectedTerm] = useState(null)

  const recalcLastDeduction = (firstDate, term, chargeOverride) => {
  if (!firstDate) return
  const charge =
    chargeOverride ?? form.getFieldValue("charge_per_installment")
  const installment = form.getFieldValue("installment")
  const loanAmount = form.getFieldValue("loan_amount")
  const baseAmount = (!isEmployeeLogin && installment && parseFloat(installment) > 0)
    ? installment
    : loanAmount
  if (!charge || !baseAmount || parseFloat(charge) === 0) return

  const numDeductions = Math.ceil(
    parseFloat(baseAmount) / parseFloat(charge)
  )
    let date = dayjs(firstDate)
    let count = 1

    while (count < numDeductions) {
      if (term === "per_payroll") {
        if (date.date() === 15) {
          date = date.endOf("month").startOf("day")
        } else {
          date = date.add(1, "month").date(15)
        }
      } else {
        date = date.add(1, "month").endOf("month").startOf("day")
      }
      count++
    }

    form.setFieldsValue({ estimated_last_deduction_date: date })
  }

  useEffect(() => {
    async function fetchUser() {
      const response = await UsePostRequest("users/get", {
        user_id: getUser(),
        token: getToken(),
      })
      if (response.data && response.data[0]) {
        setApproverName(response.data[0].name || "")
      }
    }
    fetchUser()
  }, [])

  useEffect(() => {
  async function fetchEmployees() {
    const response = await UsePostRequest("employees/get_all", {})
    if (response.data) {
      setAllEmployees(response.data)
      if (isEmployeeLogin && employeeId) {
        const emp = response.data.find((e) => String(e.id) === String(employeeId))
        if (emp) {
          // fetch from users table instead
          const userRes = await UsePostRequest("users/get", { employee_id: emp.id })
          if (userRes.data && userRes.data[0]) {
            setSelectedEmployeeName(capitalizeEachWord(userRes.data[0].name || ""))
          }
          setSelectedPosition(emp.position_name || "")
          form.setFieldsValue({
            employee_id: emp.id,
            position: emp.position_name || "",
          })
        }
      }
    }
  }
  fetchEmployees()
}, [])

  const handleEmployeeChange = (value) => {
  const emp = allEmployees.find((e) => String(e.id) === String(value))
  if (emp) {
    UsePostRequest("users/get", { employee_id: emp.id }).then((userRes) => {
      if (userRes.data && userRes.data[0]) {
        setSelectedEmployeeName(capitalizeEachWord(userRes.data[0].name || ""))
      }
    })
    setSelectedPosition(emp.position_name || "")
    form.setFieldsValue({ position: emp.position_name || "" })
  }
}

  const loanTypes = [
    { value: "sss_salary_loan", label: "SSS Salary Loan" },
    { value: "sss_calamity_loan", label: "SSS Calamity Loan" },
    { value: "company_loan", label: "Company Loan" },
    { value: "cooperative_loan", label: "Cooperative Loan" },
    { value: "pag_ibig_loan", label: "Pag-IBIG Loan" },
    { value: "laptop_loan", label: "Laptop Loan" },
    { value: "cash_advance", label: "Cash Advance" },
    { value: "phone_loan", label: "Phone Loan" },
    { value: "calamity_loan", label: "Calamity Loan" },
    { value: "others", label: "Others" },
  ]

  const termTypes = [
    { value: "per_payroll", label: "Per Payroll" },
    { value: "end_month", label: "End Month" },
  ]

  const onFinish = (formInputs) => {
    const plainValues = JSON.parse(JSON.stringify(formInputs))
    plainValues.issue_date = formInputs.issue_date
      ? formInputs.issue_date.format(dateFormat)
      : null
    plainValues.date_needed = formInputs.date_needed
      ? formInputs.date_needed.format(dateFormat)
      : null
    plainValues.first_deduction_date = formInputs.first_deduction_date
      ? formInputs.first_deduction_date.format(dateFormat)
      : null
    plainValues.estimated_last_deduction_date =
      formInputs.estimated_last_deduction_date
        ? formInputs.estimated_last_deduction_date.format(dateFormat)
        : null
    plainValues.status = isEmployeeLogin ? "pending" : "approved"
    plainValues.requester_signature = requesterSig
    plainValues.approver_signature = approverSig
    plainValues.approver_name = isEmployeeLogin ? "" : approverName
    if (isEmployeeLogin) {
    plainValues.installment = 0
  }
    callPost("employee_loans/add", plainValues, false)
  }

  return (
    <Navbar>
      <Container className="print-report-page p-3 view-page">
        {/* Back button */}
        <Row className="mb-3 noPrint">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/loans")}
          >
            Back to Loans
          </Button>
        </Row>

        <div ref={componentRef}>
          {/* ── Header ── */}
          <Row className="px-1 ms-2 align-items-center">
            <img src={logo2} alt="logo" className="payslip-logo me-3" />
            <Col>
              <Row className="bold">{window.$company_name}</Row>
              <Row className="tiny-text">{window.$company_address}</Row>
            </Col>
          </Row>

          <Divider className="black-divider mt-2 mb-2" />

          <Row className="d-flex justify-content-center mb-3">
            <span className="primary-color bold uppercase">
              CASH ADVANCE/LOAN FORM
            </span>
          </Row>

          {/* ── Form ── */}
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{ issue_date: dayjs() }}
          >
            {/* ── Employee Info ── */}
            <SectionHeader title="EMPLOYEE INFORMATION" />
            <Row gutter={[16, 0]} className="px-1">
              <Col xs={24} lg={12}>
                {isEmployeeLogin ? (
                  <Form.Item label="Employee Name">
                    <Input value={selectedEmployeeName || ""} readOnly className="secondary-bg" />
                    <Form.Item name="employee_id" hidden>
                      <Input />
                    </Form.Item>
                  </Form.Item>
                ) : (
                  <Form.Item
                    name="employee_id"
                    label="Employee Name"
                    rules={[{ required: true, message: "Required" }]}
                  >
                    <Select
                      options={allEmployees.map((e) => ({
                        value: e.id,
                        label: capitalizeEachWord(
                          `${e.first_name} ${
                            e.middle_name ? e.middle_name.charAt(0) + "." : ""
                          } ${e.last_name}`
                        ),
                      }))}
                      showSearch
                      placeholder="Select employee"
                      filterOption={(input, option) =>
                        option.label?.toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={handleEmployeeChange}
                    />
                  </Form.Item>
                )}
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="issue_date"
                  label="Request Date"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker format={dateFormat} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item name="position" label="Position">
                  <Input value={selectedPosition} readOnly className="secondary-bg" />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item name="area_dept" label="Area/Dept">
                  <Input placeholder="Enter area/dept" />
                </Form.Item>
              </Col>
            </Row>

            {/* ── Request Details ── */}
            <SectionHeader title="REQUEST DETAILS" />
            <Row gutter={[16, 0]} className="px-1">
              <Col xs={24} lg={12}>
                <Form.Item
                  name="loan_type"
                  label="Loan Type"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select options={loanTypes} placeholder="Select loan type" />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="loan_amount"
                  label="Amount"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="reason"
                  label="Purpose"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item name="date_needed" label="Date Needed">
                  <DatePicker format={dateFormat} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            {/* ── Salary Deduction Details ── */}
            <SectionHeader title="SALARY DEDUCTION DETAILS" />
            <Row gutter={[16, 0]} className="px-1">
              <Col xs={24} lg={12}>
                <Form.Item
                  name="term"
                  label="Term"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Select
                    options={termTypes}
                    placeholder="Select term first"
                    onChange={(val) => {
                      setSelectedTerm(val)
                      form.setFieldsValue({
                        first_deduction_date: null,
                        estimated_last_deduction_date: null,
                      })
                      if (val === "end_month") {
                        const lastDay = dayjs().endOf("month")
                        form.setFieldsValue({ first_deduction_date: lastDay })
                        recalcLastDeduction(lastDay, val)
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                name="installment"
                label="Total Approved Amount"
                rules={[{ required: !isEmployeeLogin, message: "Total Approved Amount is required" }]}
              >
                <Input
                  type="number"
                  onWheel={(e) => e.target.blur()}
                  placeholder={!selectedTerm ? "Select term first" : "0.00"}
                  readOnly={isEmployeeLogin || !selectedTerm}
                  className={isEmployeeLogin || !selectedTerm ? "secondary-bg" : ""}
                  onChange={(e) => {
                    const firstDate = form.getFieldValue("first_deduction_date")
                    if (firstDate && selectedTerm) recalcLastDeduction(firstDate, selectedTerm)
                  }}
                />
              </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="charge_per_installment"
                  label="Deduction per Payday"
                  rules={[{ required: true, message: "Required" }]}
                >
                  <Input
                    type="number"
                    onWheel={(e) => e.target.blur()}
                    placeholder="0.00"
                    disabled={!selectedTerm}
                    onChange={(e) => {
                      const firstDate = form.getFieldValue(
                        "first_deduction_date"
                      )
                      if (firstDate)
                        recalcLastDeduction(
                          firstDate,
                          selectedTerm,
                          e.target.value
                        )
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                name="first_deduction_date"
                label="First Deduction Date"
                rules={[{ required: true, message: "Required" }]}
                >
                  <DatePicker
                    format={dateFormat}
                    style={{ width: "100%" }}
                    disabled={!selectedTerm || selectedTerm === "end_month"}
                    onChange={(val) => recalcLastDeduction(val, selectedTerm)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} lg={12}>
                <Form.Item
                  name="estimated_last_deduction_date"
                  label="Estimated Last Deduction Date"
                >
                  <DatePicker
                    format={dateFormat}
                    style={{ width: "100%" }}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* ── Acknowledgement ── */}
            <SectionHeader title="ACKNOWLEDGEMENT" />
            <Row className="px-1 mb-3">
              <p>
                I hereby acknowledge that the amount stated above is a cash
                advance/loan from the company. I authorize the company to deduct
                the specified amount from my salary every payday until the loan
                is fully paid.
              </p>
            </Row>

            {/* ── Signatures ── */}
            <Row gutter={[24, 16]} className="px-1">
              <SignatureBox
                label="Requested by:"
                sig={requesterSig}
                name={selectedEmployeeName}
                onAdd={() => setShowRequesterSigModal(true)}
              />
              <SignatureBox
              label="Approved by:"
              sig={approverSig}
              name={isEmployeeLogin ? "" : (approverName ? capitalizeEachWord(approverName) : "")}
              onAdd={isEmployeeLogin ? null : () => setShowApproverSigModal(true)}
            />
            </Row>

            {/* ── Action Buttons ── */}
            <Row className="d-flex justify-content-end my-4 gap-2 noPrint">
              <Button
                danger
                onClick={() => navigate("/loans")}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => form.submit()}
                loading={isLoading}
              >
                Submit
              </Button>
            </Row>
          </Form>
        </div>
      </Container>

      {/* ── Signature Modals ── */}
      <SignaturePadModal
        open={showRequesterSigModal}
        title="Draw Signature"
        onCancel={() => setShowRequesterSigModal(false)}
        onSubmit={(sig) => {
          setRequesterSig(sig)
          setShowRequesterSigModal(false)
        }}
      />
      <SignaturePadModal
        open={showApproverSigModal}
        title="Draw Signature"
        onCancel={() => setShowApproverSigModal(false)}
        onSubmit={(sig) => {
          setApproverSig(sig)
          setShowApproverSigModal(false)
        }}
      />
    </Navbar>
  )
}